import {
  ICON_LARGE_STYLE,
  SphereAvatar,
} from "@components/header/sphere-avatar";
import { UpdateProfilePictureDialog } from "@components/user-info/update-profile-picture-dialog";
import { BaseMemberProps } from "@custom-types/member-types";
import { Box } from "@mui/material";
import { useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { blue, neutral, TeamIcon, CameraIcon } from "@flat-ui";
import { getPendingUserIcon, getUserInitials } from "@utils/user-utils";
import { useState } from "react";
import { teamDisplayProperties } from "@components/table/teams/teams-table-utils";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface Props extends BaseMemberProps {
  /** Optional team if the avatar is displayed for a team  */
  team?: FaroCoreApiTypes.Team;
}

/** Renders SphereAvatar component with ability to update avatar */
export function SphereUpdatableAvatar({ member, team }: Props): JSX.Element {
  const loggedInUser = useAppSelector(loggedInUserSelector);

  const [isAvatarHovering, setIsAvatarHovering] = useState<boolean>(false);
  const [isProfilePictureDialogOpen, setIsProfilePictureDialogOpen] =
    useState<boolean>(false);

  const isCurrentUser = member.identity === loggedInUser?.identity;

  // Return the normal SphereAvatar if member is not current user
  if (member.kind === "user-group") {
    const teamCategory = team?.category?.kind ?? "default";

    return (
      <SphereAvatar
        size="x-large"
        icon={<TeamIcon style={ICON_LARGE_STYLE} />}
        backgroundColor={teamDisplayProperties[teamCategory].color}
      />
    );
  }

  if (!isCurrentUser) {
    return (
      <SphereAvatar
        size="x-large"
        initials={getUserInitials(member)}
        icon={getPendingUserIcon(member, "large")}
        src={member.thumbnailUrl}
      />
    );
  }

  return (
    <>
      <Box
        component="div"
        onMouseEnter={() => setIsAvatarHovering(true)}
        onMouseLeave={() => setIsAvatarHovering(false)}
        onClick={() => setIsProfilePictureDialogOpen(true)}
        sx={{
          width: "fit-content",
          cursor: "pointer",
        }}
      >
        {isAvatarHovering ? (
          <SphereAvatar
            size="x-large"
            icon={<CameraIcon sx={{ ...ICON_LARGE_STYLE }} />}
            iconColor={blue[500]}
            shouldUseSimplifiedAvatar={!isCurrentUser}
            backgroundColor={neutral[200]}
          />
        ) : (
          <SphereAvatar
            size="x-large"
            initials={getUserInitials(loggedInUser)}
            iconColor={blue[500]}
            src={loggedInUser.thumbnailUrl}
            shouldUseSimplifiedAvatar={!isCurrentUser}
            backgroundColor={neutral[400]}
          />
        )}
      </Box>

      {isProfilePictureDialogOpen && (
        <UpdateProfilePictureDialog
          user={loggedInUser}
          isDialogOpen={isProfilePictureDialogOpen}
          setIsDialogOpen={setIsProfilePictureDialogOpen}
        />
      )}
    </>
  );
}
